import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined'
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'

import Langs, { Localize } from '../../../app/lang/langs'
import { vlearnGrid, vlearnSurface, vlearnType, VLearnProgressRing, VLearnStatTile } from '../vlearnUi'

/**
 * V-Learning — the result screen.
 *
 * ── Why the score may simply not be there ───────────────────────────────────────────
 *
 * `VLearnQuizController.scoreFields()` omits `score_points`, `max_points`, `percent` and
 * `passed` from the response entirely when the quiz's `show_score` is off. That is the fix
 * for V-Sécurité's defect: `QuizResults.js` there hides the score with an `if` while the
 * payload carries it anyway, so the setting is one devtools panel away from meaning
 * nothing. Here the absence is the enforcement, and this component therefore treats a
 * missing `percent` as a first-class state — no ring, no number, no inferred verdict — and
 * never as an error or a zero.
 *
 * ── Why the points line matters next to the percentage ──────────────────────────────
 *
 * Questions carry weights, so "85%" alone does not tell a learner which questions they
 * lost. `17 / 20 points` beside it does, and both fields arrive together or not at all.
 *
 * @param {object} props
 * @param {object} props.result the `submitVLearnAttempt` payload
 * @param {object} props.quiz the learner-facing quiz metadata from `getVLearnQuizForLearner`
 * @param {Function} props.onRetake
 * @param {Function} props.onReview
 * @param {Function} props.onExit
 */
export default function QuizResult({ result, quiz, onRetake, onReview, onExit }) {
    const lang = useSelector((state) => state.settings.lang)

    const attempt = result && result.attempt ? result.attempt : null

    /**
     * Whether the payload carries a score at all.
     *
     * Checked on the type rather than on truthiness, because 0% is a real, renderable
     * result and `!percent` would quietly turn a total failure into a withheld score.
     */
    const percent = attempt ? attempt.percent : undefined
    const hasScore = typeof percent === 'number'
    const passed = attempt ? attempt.passed : undefined

    const hasPoints = attempt
        && typeof attempt.score_points === 'number'
        && typeof attempt.max_points === 'number'

    /**
     * How many questions were marked right.
     *
     * `results` is in the payload only when the reveal policy allows it — the controller
     * omits the key rather than sending an empty array, precisely so a client cannot read
     * "graded, nothing correct" out of a withheld correction.
     */
    const correctCount = useMemo(() => {
        if (!result || !Array.isArray(result.results)) return null
        return result.results.filter((entry) => entry.is_correct).length
    }, [result])

    /**
     * Whether another attempt is permitted.
     *
     * The submit response does not carry the allowance — it reports the attempt, not the
     * quiz — so it is read from `quiz.max_attempts` (0 = unlimited) against the attempt
     * number the server just closed. A learner who passed is offered nothing to retake:
     * the assessment is done, and inviting them to sit it again is inviting them to lose
     * the pass they already hold.
     */
    const canRetake = useMemo(() => {
        if (typeof onRetake !== 'function' || !attempt) return false
        if (passed === true) return false
        const maxAttempts = quiz ? Number(quiz.max_attempts) || 0 : 0
        if (!maxAttempts) return true
        return Number(attempt.attempt_no) < maxAttempts
    }, [attempt, onRetake, passed, quiz])

    const canReview = !!(result && result.canReview) && typeof onReview === 'function'

    if (!result || !attempt) return null

    return (
        <Box component="section" aria-labelledby="vlearn-result-title" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            <Box sx={{ ...vlearnSurface.card, padding: { xs: '24px 20px', sm: '36px 32px' } }}>

                {hasScore ? (
                    <Stack alignItems="center" spacing={2}>
                        <VLearnProgressRing
                            percent={percent}
                            size={132}
                            thickness={9}
                            status={passed === true ? 'completed' : passed === false ? 'overdue' : undefined}
                        />
                        <Stack direction="row" alignItems="center" spacing={1}>
                            {passed === true ? (
                                <CheckCircleRoundedIcon aria-hidden="true" sx={{ color: 'var(--green, rgb(58,156,96))', fontSize: 28 }} />
                            ) : passed === false ? (
                                <CancelRoundedIcon aria-hidden="true" sx={{ color: 'var(--red, rgb(251,105,98))', fontSize: 28 }} />
                            ) : null}
                            <Typography id="vlearn-result-title" component="h2" sx={{
                                fontSize: { xs: '1.3rem', sm: '1.5rem' },
                                fontWeight: 700,
                                color: passed === true ? '#1F7A4C' : passed === false ? '#B3271F' : 'var(--text-primary, #111318)'
                            }}>
                                <Langs str={passed === true ? 'VLEARN_RESULT_PASSED' : 'VLEARN_RESULT_FAILED'} />
                            </Typography>
                        </Stack>
                        {quiz && quiz.title ? (
                            <Typography sx={{ ...vlearnType.meta, textAlign: 'center' }}>{quiz.title}</Typography>
                        ) : null}
                    </Stack>
                ) : (
                    /* No ring and no number: the score is not being hidden here, it was never
                       sent. Saying "recorded" is the whole truth this screen has. */
                    <Stack alignItems="center" spacing={2}>
                        <Box sx={{
                            width: 76, height: 76, borderRadius: '22px',
                            backgroundColor: 'rgba(58,156,96,0.10)',
                            border: '1px solid rgba(58,156,96,0.22)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <TaskAltRoundedIcon aria-hidden="true" sx={{ fontSize: 36, color: 'var(--green, rgb(58,156,96))' }} />
                        </Box>
                        <Typography id="vlearn-result-title" component="h2" sx={{
                            fontSize: { xs: '1.15rem', sm: '1.3rem' }, fontWeight: 700, textAlign: 'center',
                            color: 'var(--text-primary, #111318)'
                        }}>
                            <Langs str="VLEARN_RESULT_RECORDED_TITLE" />
                        </Typography>
                        <Typography sx={{ ...vlearnType.body, textAlign: 'center', maxWidth: 460 }}>
                            <Langs str="VLEARN_RESULT_RECORDED_BODY" />
                        </Typography>
                    </Stack>
                )}

                {result.expired ? (
                    <Box sx={{
                        mt: 3, padding: '14px 16px', borderRadius: '12px', display: 'flex', gap: 1.25,
                        backgroundColor: 'rgba(255,144,21,0.09)', border: '1px solid rgba(255,144,21,0.28)'
                    }}>
                        <WarningAmberRoundedIcon aria-hidden="true" sx={{ color: '#B25E00', fontSize: 20, flexShrink: 0, mt: '1px' }} />
                        <Typography sx={{ ...vlearnType.body, color: '#8a4a00' }}>
                            <Langs str="VLEARN_RESULT_EXPIRED_NOTICE" />
                        </Typography>
                    </Box>
                ) : null}

                <Box sx={{ ...vlearnGrid.tiles, mt: 3 }}>
                    <VLearnStatTile
                        label={<Langs str="VLEARN_RESULT_STAT_ANSWERED" />}
                        value={`${Number(result.questions_answered) || 0} / ${Number(result.questions_total) || 0}`}
                        hint={Number(result.questions_total) - (Number(result.questions_answered) || 0) > 0
                            ? Localize('VLEARN_RESULT_STAT_BLANK_HINT', lang)
                                .replace('%n%', Number(result.questions_total) - (Number(result.questions_answered) || 0))
                            : null}
                    />
                    {correctCount !== null ? (
                        <VLearnStatTile
                            label={<Langs str="VLEARN_RESULT_STAT_CORRECT" />}
                            value={`${correctCount} / ${Number(result.questions_total) || 0}`}
                        />
                    ) : null}
                    {/* Only rendered when BOTH fields are present, so a weighted paper never
                        shows a numerator without its denominator. */}
                    {hasPoints ? (
                        <VLearnStatTile
                            label={<Langs str="VLEARN_RESULT_STAT_POINTS" />}
                            value={`${formatPoints(attempt.score_points)} / ${formatPoints(attempt.max_points)}`}
                        />
                    ) : null}
                    {quiz && quiz.passing_percent ? (
                        <VLearnStatTile
                            label={<Langs str="VLEARN_RESULT_STAT_PASS_MARK" />}
                            value={`${Number(quiz.passing_percent)}%`}
                        />
                    ) : null}
                </Box>

                {hasScore && passed === true ? (
                    <Box sx={{
                        mt: 2.5, padding: '16px 18px', borderRadius: '12px', display: 'flex', gap: 1.25,
                        backgroundColor: 'rgba(58,156,96,0.08)', border: '1px solid rgba(58,156,96,0.22)'
                    }}>
                        <EmojiEventsOutlinedIcon aria-hidden="true" sx={{ color: '#1F7A4C', fontSize: 22, flexShrink: 0 }} />
                        <Typography sx={{ ...vlearnType.body, color: '#1F7A4C', fontWeight: 600 }}>
                            <Langs str="VLEARN_RESULT_CONGRATS" />
                        </Typography>
                    </Box>
                ) : null}

                {hasScore && passed === false && quiz ? (
                    <Box sx={{
                        mt: 2.5, padding: '16px 18px', borderRadius: '12px', display: 'flex', gap: 1.25,
                        backgroundColor: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.08)'
                    }}>
                        <WarningAmberRoundedIcon aria-hidden="true" sx={{ color: 'var(--text-muted, #8b92a0)', fontSize: 22, flexShrink: 0 }} />
                        <Typography sx={{ ...vlearnType.body }}>
                            <Langs
                                str={canRetake ? 'VLEARN_RESULT_FAILED_RETRY' : 'VLEARN_RESULT_FAILED_NO_RETRY'}
                                params={{ mark: Number(quiz.passing_percent) }}
                            />
                        </Typography>
                    </Box>
                ) : null}

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    justifyContent="flex-end"
                    sx={{ mt: 3.5 }}
                >
                    <Button
                        variant="text"
                        onClick={onExit}
                        startIcon={<ArrowBackRoundedIcon />}
                        sx={quietButtonSx}
                    >
                        <Langs str="VLEARN_RESULT_BTN_EXIT" />
                    </Button>

                    {/* Review is offered only on the server's own `canReview`, which comes
                        out of `mayRevealAnswers` — the same function that answers the 403.
                        A button drawn from anything else would open a screen the endpoint
                        refuses. */}
                    {canReview ? (
                        <Button
                            variant="outlined"
                            onClick={onReview}
                            startIcon={<VisibilityOutlinedIcon />}
                            sx={outlinedButtonSx}
                        >
                            <Langs str="VLEARN_RESULT_BTN_REVIEW" />
                        </Button>
                    ) : null}

                    {canRetake ? (
                        <Button
                            variant="contained"
                            disableElevation
                            onClick={onRetake}
                            startIcon={<ReplayRoundedIcon />}
                            sx={primaryButtonSx}
                        >
                            <Langs str="VLEARN_RESULT_BTN_RETAKE" />
                        </Button>
                    ) : null}
                </Stack>
            </Box>
        </Box>
    )
}

/** Points are `decimal(8,2)`; a whole number should not read as "17.00". */
function formatPoints(value) {
    const number = Number(value)
    if (!Number.isFinite(number)) return '0'
    return Number.isInteger(number) ? String(number) : number.toFixed(2).replace(/0$/, '')
}

const primaryButtonSx = {
    borderRadius: '12px',
    px: 3,
    minHeight: 46,
    textTransform: 'none',
    fontWeight: 650,
    fontSize: '0.92rem',
    backgroundColor: 'var(--orange, rgb(255,144,21))',
    color: '#fff',
    '&:hover': { backgroundColor: '#e07d0d' },
    '&:focus-visible': { outline: '3px solid rgba(255,144,21,0.45)', outlineOffset: '2px' }
}

const outlinedButtonSx = {
    borderRadius: '12px',
    px: 3,
    minHeight: 46,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
    color: 'var(--text-secondary, #4b5263)',
    borderColor: 'rgba(0,0,0,0.14)',
    '&:hover': { borderColor: 'rgba(0,0,0,0.28)', backgroundColor: 'rgba(0,0,0,0.03)' },
    '&:focus-visible': { outline: '3px solid rgba(255,144,21,0.45)', outlineOffset: '2px' }
}

const quietButtonSx = {
    borderRadius: '12px',
    minHeight: 46,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.88rem',
    color: 'var(--text-secondary, #4b5263)',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
    '&:focus-visible': { outline: '3px solid rgba(255,144,21,0.45)', outlineOffset: '2px' }
}
